import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, GeoJSON, useMap, useMapEvents, Marker, Popup, Tooltip, CircleMarker, Polyline } from 'react-leaflet';
import L from 'leaflet';
import { useAuth } from '../contexts/AuthContext';
import { segments as segmentsApi } from '../services/api';
import SegmentPanel from '../components/SegmentPanel';
import useOnlineStatus from '../hooks/useOnlineStatus';
import { EXPANSION_STATUSES, expansionStatusLabel } from '../utils/categories';
import { footingColor, FOOTING_BANDS, FOOTING_UNRATED_COLOR } from '../utils/footing';
import { segmentName } from '../utils/segments';

// Maintenance status colors
const STATUS_COLORS = {
  clear: '#2d6a4f', light_weeds: '#f4a261', heavy_weeds: '#e76f51',
  other_maintenance: '#e63946', closed_impassable: '#6c757d',
};
const NEIGHBOR_TRAIL_COLOR = '#2d6a4f';
// Mirrors VOLUNTEER_ONLY_CATEGORIES in backend/src/utils/segmentVisibility.js.
const NEIGHBOR_HIDDEN_CATEGORIES = ['expansion', 'courtesy'];
// Reserved for segments with no access agreement at all — the thing worth
// spotting when colouring by agreement type.
const NO_AGREEMENT_COLOR = '#dc3545';
const CATEGORY_COLORS = {
  internal: null, external: '#4895ef',
  courtesy: '#5a6268', expansion: '#7b3fc4',
};
const CATEGORY_LABELS = {
  internal: 'LCPOA Internal Trails', external: 'Roadside Trails',
  courtesy: 'Courtesy Maintenance', expansion: 'Expansion (proposed)',
};
const COLOR_MODE_LABELS = {
  maintenance: 'Maintenance Status',
  access_type: 'Access Agreement Type',
  footing: 'Footing Quality',
};
// Dash patterns that say what a line *is*, independent of its colour.
const CATEGORY_DASHES = { courtesy: '2 6', expansion: '10 6' };

function segmentStyle(segment, colorMode, highlightTrail) {
  const isHighlighted = highlightTrail && segment.trail_name === highlightTrail;
  let color;

  if (colorMode === 'neighbor') {
    // Neighbors: roadside = blue, everything else = uniform green
    color = segment.category === 'external' ? CATEGORY_COLORS.external : NEIGHBOR_TRAIL_COLOR;
  } else if (colorMode === 'access_type') {
    color = segment.access_type_color || NO_AGREEMENT_COLOR;
  } else if (colorMode === 'footing') {
    color = footingColor(segment.footing_quality);
  } else if (segment.category !== 'internal') {
    color = CATEGORY_COLORS[segment.category] || '#264653';
  } else {
    color = STATUS_COLORS[segment.maintenance_status] || '#264653';
  }

  // Neighbors get clean uniform styling (no dashes, no opacity variation)
  if (colorMode === 'neighbor') {
    return {
      color,
      weight: isHighlighted ? 7 : 4,
      opacity: 0.85,
      dashArray: null,
    };
  }

  return {
    color,
    weight: isHighlighted ? 7 : (segment.has_active_concern ? 5 : 4),
    opacity: segment.category === 'courtesy' ? 0.65 : 0.85,
    dashArray: segment.has_active_concern ? '8 4' : (CATEGORY_DASHES[segment.category] || null),
  };
}

// Invisible wider click zone per req #9
function hitStyle() {
  return { weight: 20, opacity: 0, stroke: true };
}

// Zoom controls position per req #14 — disable default, add at bottomright
function ZoomControl() {
  const map = useMap();
  useEffect(() => {
    // Remove the default top-left zoom control
    map.zoomControl.remove();
    // Add a new one at bottom-right
    L.control.zoom({ position: 'bottomright' }).addTo(map);
  }, [map]);
  return null;
}

// One-shot: fits bounds then signals parent to stop rendering it
function FitBounds({ segments, onComplete }) {
  const map = useMap();
  useEffect(() => {
    const coords = [];
    segments.filter(s => s.geometry).forEach(s => {
      const extract = (c) => { if (typeof c[0] === 'number') coords.push([c[1], c[0]]); else c.forEach(extract); };
      if (s.geometry.coordinates) extract(s.geometry.coordinates);
    });
    if (coords.length > 0) map.fitBounds(coords, { padding: [50, 50] });
    if (onComplete) onComplete();
  }, []); // Run exactly once on mount
  return null;
}

// GPS location per req #8
function UserLocation() {
  const [pos, setPos] = useState(null);
  useEffect(() => {
    if (!navigator.geolocation) return;
    const watchId = navigator.geolocation.watchPosition(
      (p) => setPos([p.coords.latitude, p.coords.longitude]),
      () => {}, { enableHighAccuracy: true, maximumAge: 10000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);
  if (!pos) return null;
  return (
    <CircleMarker center={pos} radius={8} pathOptions={{ color: '#2563eb', fillColor: '#3b82f6', fillOpacity: 0.9, weight: 3 }}>
      <Popup>Your location</Popup>
    </CircleMarker>
  );
}

// Trail name labels with zoom-based density per req #1
function TrailLabels({ segments, zoom }) {
  // Group by trail name and compute center
  const trails = {};
  segments.filter(s => s.geometry && s.category === 'internal').forEach(s => {
    if (!trails[s.trail_name]) trails[s.trail_name] = { coords: [], count: 0, totalLength: 0 };
    trails[s.trail_name].count++;
    trails[s.trail_name].totalLength += parseFloat(s.length_feet) || 0;
    const extract = (c) => { if (typeof c[0] === 'number') trails[s.trail_name].coords.push(c); else c.forEach(extract); };
    if (s.geometry.coordinates) extract(s.geometry.coordinates);
  });

  // Determine which labels to show based on zoom
  const labels = Object.entries(trails).map(([name, data]) => {
    if (data.coords.length === 0) return null;
    const mid = data.coords[Math.floor(data.coords.length / 2)];
    return { name, lat: mid[1], lng: mid[0], count: data.count, length: data.totalLength };
  }).filter(Boolean);

  // Sort by segment count (larger trails first)
  labels.sort((a, b) => b.count - a.count);

  // Zoom-based density
  let maxLabels;
  if (zoom <= 13) maxLabels = 3;
  else if (zoom <= 14) maxLabels = 8;
  else if (zoom <= 15) maxLabels = 15;
  else maxLabels = labels.length;

  const visible = labels.slice(0, maxLabels);

  return visible.map(l => (
    <CircleMarker key={l.name} center={[l.lat, l.lng]} radius={0} pathOptions={{ opacity: 0 }}>
      <Tooltip permanent direction="center" className="trail-label">
        {l.name.replace(' Trail', '')}
      </Tooltip>
    </CircleMarker>
  ));
}

// Parcel APN labels at high zoom
function ParcelLabels({ parcelData, zoom }) {
  if (!parcelData || zoom < 17) return null;

  const labels = parcelData.features.map((f, i) => {
    const apn = f.properties?.APN;
    if (!apn) return null;
    // Compute centroid from first ring of coordinates
    const coords = f.geometry.type === 'MultiPolygon'
      ? f.geometry.coordinates[0][0]
      : f.geometry.coordinates[0];
    if (!coords || coords.length === 0) return null;
    let latSum = 0, lngSum = 0;
    coords.forEach(c => { lngSum += c[0]; latSum += c[1]; });
    const lat = latSum / coords.length;
    const lng = lngSum / coords.length;
    return { apn, lat, lng, key: i };
  }).filter(Boolean);

  return labels.map(l => (
    <CircleMarker key={l.key} center={[l.lat, l.lng]} radius={0} pathOptions={{ opacity: 0 }}>
      <Tooltip permanent direction="center" className="parcel-label">
        {l.apn.replace(/^(\d{3})(\d{3})(\d+)$/, '$1-$2-$3')}
      </Tooltip>
    </CircleMarker>
  ));
}

// Fly to a trail's bounds when selected
function FlyToTrail({ segments, trailName }) {
  const map = useMap();
  useEffect(() => {
    if (!trailName) return;
    const coords = [];
    segments.filter(s => s.geometry && s.trail_name === trailName).forEach(s => {
      const extract = (c) => { if (typeof c[0] === 'number') coords.push([c[1], c[0]]); else c.forEach(extract); };
      if (s.geometry.coordinates) extract(s.geometry.coordinates);
    });
    if (coords.length > 0) map.flyToBounds(coords, { padding: [50, 50], maxZoom: 17 });
  }, [trailName, segments, map]);
  return null;
}

// Fly to a parcel when the map is opened with ?apn=
function FlyToParcel({ feature }) {
  const map = useMap();
  useEffect(() => {
    if (!feature) return;
    const coords = [];
    const extract = (c) => { if (typeof c[0] === 'number') coords.push([c[1], c[0]]); else c.forEach(extract); };
    extract(feature.geometry.coordinates);
    if (coords.length > 0) map.flyToBounds(coords, { padding: [60, 60], maxZoom: 18 });
  }, [feature, map]);
  return null;
}

// Zoom to one segment when the map is opened with ?segment=
function FlyToSegment({ segment }) {
  const map = useMap();
  useEffect(() => {
    if (!segment || !segment.geometry) return;
    const coords = [];
    const extract = (c) => { if (typeof c[0] === 'number') coords.push([c[1], c[0]]); else c.forEach(extract); };
    extract(segment.geometry.coordinates);
    // maxZoom 16 keeps some surrounding context — close, but not nose-to-the-dirt.
    if (coords.length > 0) map.flyToBounds(coords, { padding: [70, 70], maxZoom: 16 });
  }, [segment, map]);
  return null;
}

// Track zoom level
function ZoomTracker({ onZoomChange }) {
  const map = useMap();
  useEffect(() => {
    const handler = () => onZoomChange(map.getZoom());
    map.on('zoomend', handler);
    onZoomChange(map.getZoom());
    return () => map.off('zoomend', handler);
  }, [map, onZoomChange]);
  return null;
}

// Click-to-trace drawing for a proposed (expansion) segment. Each map click
// adds a vertex; double-click zoom is off meanwhile so fast taps don't zoom.
function DrawLayer({ points, onAdd }) {
  const map = useMap();
  useEffect(() => {
    map.doubleClickZoom.disable();
    return () => map.doubleClickZoom.enable();
  }, [map]);
  useMapEvents({ click: (e) => onAdd([e.latlng.lng, e.latlng.lat]) });

  const color = CATEGORY_COLORS.expansion;
  return (
    <>
      {points.length > 1 && (
        <Polyline positions={points.map(([lng, lat]) => [lat, lng])}
          pathOptions={{ color, weight: 4, dashArray: CATEGORY_DASHES.expansion }} interactive={false} />
      )}
      {points.map(([lng, lat], i) => (
        <CircleMarker key={i} center={[lat, lng]} radius={5} interactive={false}
          pathOptions={{ color, weight: 2, fillColor: '#fff', fillOpacity: 1 }} />
      ))}
    </>
  );
}

export default function MapPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isVolunteer, effectiveRole } = useAuth();
  const { isOnline } = useOnlineStatus();
  const [segments, setSegments] = useState([]);
  const [autoSelectId] = useState(() => searchParams.get('segment'));
  // ?apn=929-120-010 focuses a parcel. The layer stores APNs without dashes.
  const [targetApn] = useState(() => (searchParams.get('apn') || '').replace(/-/g, ''));
  const [deepLinkSegment, setDeepLinkSegment] = useState(null);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [detailSegment, setDetailSegment] = useState(null);
  const [highlightTrail, setHighlightTrail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(14);
  const [boundsSet, setBoundsSet] = useState(false);
  const [offlineDismissed, setOfflineDismissed] = useState(false);
  const [layersCollapsed, setLayersCollapsed] = useState(false);
  const [legendCollapsed, setLegendCollapsed] = useState(false);
  const [showParcels, setShowParcels] = useState(false);
  const [parcelData, setParcelData] = useState(null);
  const [baseLayer, setBaseLayer] = useState('street');
  const [selectedTrailFilter, setSelectedTrailFilter] = useState('');
  const isNeighbor = effectiveRole === 'neighbor';
  const [colorMode, setColorMode] = useState(isVolunteer ? 'maintenance' : 'neighbor');
  // Role preview swaps the effective role without remounting this page, so the
  // mode must be derived rather than read from state set at mount — otherwise
  // previewing as a neighbor keeps maintenance colours and dashes.
  const activeColorMode = isNeighbor ? 'neighbor' : colorMode;
  const [visibleCategories, setVisibleCategories] = useState({
    internal: true, external: true, courtesy: true, expansion: true,
  });

  // Drawing a new expansion segment: tracing, then the details form.
  const [drawing, setDrawing] = useState(false);
  const [drawPoints, setDrawPoints] = useState([]);
  const [drawForm, setDrawForm] = useState(null);
  const [savingDraw, setSavingDraw] = useState(false);
  const [drawError, setDrawError] = useState(null);
  // Segment click handlers are bound once per render of the layer; a ref lets
  // them see that drawing started without rebinding every line.
  const drawingRef = useRef(false);
  useEffect(() => { drawingRef.current = drawing; }, [drawing]);

  const loadSegments = useCallback(async () => {
    try { setSegments(await segmentsApi.list()); } catch (err) { setError(err.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSegments(); }, [loadSegments]);

  // Auto-select segment if ?segment=id is in URL
  useEffect(() => {
    if (autoSelectId && segments.length > 0 && !detailSegment) {
      const seg = segments.find(s => s.id === autoSelectId);
      if (seg) {
        handleSegmentClick(seg);
        setDeepLinkSegment(seg);
        setSearchParams({}, { replace: true });
      }
    }
  }, [autoSelectId, segments]); // eslint-disable-line

  // An ?apn= link implies the parcel layer, so switch it on for them.
  useEffect(() => {
    if (targetApn) setShowParcels(true);
  }, [targetApn]);

  // Lazy-load parcel data when toggled on
  useEffect(() => {
    if (showParcels && !parcelData) {
      fetch('/la_cresta_parcels.geojson')
        .then(r => r.json())
        .then(setParcelData)
        .catch(err => console.error('Failed to load parcels:', err));
    }
  }, [showParcels, parcelData]);

  const handleSegmentClick = async (segment) => {
    // While tracing, a click on an existing trail is a vertex, not a selection.
    if (drawingRef.current) return;
    if (isNeighbor) {
      // Neighbor: show trail-level view, highlight entire trail
      setHighlightTrail(segment.trail_name);
      try {
        const trailData = await segmentsApi.getTrail(segment.trail_name);
        setDetailSegment({ ...trailData, isTrailView: true });
        setSelectedSegment(segment.trail_name);
      } catch (err) { setError(err.message); }
    } else {
      setHighlightTrail(null);
      try {
        const detail = await segmentsApi.get(segment.id);
        setDetailSegment(detail);
        setSelectedSegment(segment.id);
      } catch (err) { setError(err.message); }
    }
  };

  const startDrawing = () => {
    setSelectedSegment(null); setDetailSegment(null); setHighlightTrail(null);
    setDrawPoints([]); setDrawForm(null); setDrawError(null);
    setDrawing(true);
  };

  const cancelDrawing = () => {
    setDrawing(false); setDrawPoints([]); setDrawForm(null); setDrawError(null);
  };

  const saveDrawing = async () => {
    setSavingDraw(true); setDrawError(null);
    try {
      const created = await segmentsApi.createExpansion({
        ...drawForm,
        geometry: { type: 'LineString', coordinates: drawPoints },
      });
      cancelDrawing();
      setVisibleCategories(prev => ({ ...prev, expansion: true }));
      await loadSegments();
      const detail = await segmentsApi.get(created.id);
      setDetailSegment(detail);
      setSelectedSegment(created.id);
    } catch (err) {
      setDrawError(err.message);
    } finally {
      setSavingDraw(false);
    }
  };

  const handlePanelClose = () => {
    setSelectedSegment(null); setDetailSegment(null); setHighlightTrail(null);
  };

  const handleSegmentUpdate = async () => {
    await loadSegments();
    if (selectedSegment && !isNeighbor) {
      const detail = await segmentsApi.get(selectedSegment);
      setDetailSegment(detail);
    }
  };

  const toggleCategory = (cat) => setVisibleCategories(prev => ({ ...prev, [cat]: !prev[cat] }));

  if (loading) return <div className="loading">Loading trails...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  const defaultCenter = [33.535, -117.32];

  // Neighbors see LCPOA-managed segments only, grouped as Internal or Roadside.
  // The API never sends them expansion or courtesy segments (see the backend's
  // segmentVisibility.js); the filter here is for admins previewing as a
  // neighbor, who still hold the full list.
  const mapSegments = isNeighbor ? segments.filter(s => !NEIGHBOR_HIDDEN_CATEGORIES.includes(s.category)) : segments;
  let visibleSegments;
  if (isNeighbor) {
    visibleSegments = mapSegments.filter(s => s.geometry);
  } else {
    visibleSegments = segments.filter(s => s.geometry && visibleCategories[s.category]);
  }

  // The parcel a ?apn= link points at, once the layer has loaded.
  const targetParcel = (targetApn && parcelData)
    ? parcelData.features.find(f => f.properties.APN === targetApn) || null
    : null;

  const categoryCounts = {};
  segments.forEach(s => { categoryCounts[s.category] = (categoryCounts[s.category] || 0) + 1; });
  const totalInternal = mapSegments.filter(s => s.category !== 'external' && s.category !== 'expansion').length;

  return (
    <div className="map-page">
      {/* Offline banner per req #8 */}
      {!isOnline && !offlineDismissed && (
        <div className="offline-banner">
          You are in offline mode. Map tiles may be outdated.
          <button onClick={() => setOfflineDismissed(true)}>✕</button>
        </div>
      )}

      <div className={`map-container ${detailSegment ? 'with-panel' : ''} ${drawing ? 'drawing' : ''}`}>
        <MapContainer center={defaultCenter} zoom={14} className="leaflet-map" zoomControl={true}>
          {baseLayer === 'satellite' ? (
            <TileLayer
              key="satellite"
              attribution='Tiles &copy; Esri'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19}
            />
          ) : (
            <TileLayer
              key="street"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          )}
          <ZoomControl />
          <ZoomTracker onZoomChange={setZoom} />
          {!boundsSet && segments.length > 0 && !autoSelectId && (
            <FitBounds segments={segments} onComplete={() => setBoundsSet(true)} />
          )}
          {deepLinkSegment && <FlyToSegment segment={deepLinkSegment} />}
          <UserLocation />
          <TrailLabels segments={mapSegments} zoom={zoom} />
          {selectedTrailFilter && <FlyToTrail segments={segments} trailName={selectedTrailFilter} />}

          {targetParcel && (
            <>
              <FlyToParcel feature={targetParcel} />
              <GeoJSON
                key={`apn-${targetApn}`}
                data={targetParcel}
                interactive={false}
                style={() => ({ color: '#e63946', weight: 3, opacity: 1, fillColor: '#e63946', fillOpacity: 0.25 })}
              />
            </>
          )}

          {showParcels && parcelData && (
            <>
              <GeoJSON
                data={parcelData}
                interactive={false}
                style={() => ({
                  color: '#b5651d',
                  weight: 1.5,
                  opacity: 0.6,
                  fillColor: '#b5651d',
                  fillOpacity: 0.05,
                })}
              />
              <ParcelLabels parcelData={parcelData} zoom={zoom} />
            </>
          )}

          {visibleSegments.map(seg => (
            <React.Fragment key={`${seg.id}-${activeColorMode}-${highlightTrail}`}>
              {/* Invisible wider hit zone per req #9 */}
              <GeoJSON data={seg.geometry} style={hitStyle}
                onEachFeature={(f, layer) => { layer.on('click', () => handleSegmentClick(seg)); }} />
              {/* Visible trail line */}
              <GeoJSON data={seg.geometry}
                style={() => segmentStyle(seg, activeColorMode, highlightTrail)}
                onEachFeature={(f, layer) => {
                  const label = isNeighbor
                    ? seg.segment_label || seg.trail_name
                    : seg.category === 'expansion'
                    ? `${segmentName(seg)} — proposed (${expansionStatusLabel(seg.expansion_status)})`
                    : (seg.category === 'internal' || seg.segment_label
                      ? segmentName(seg)
                      : seg.description || segmentName(seg));
                  const footing = activeColorMode === 'footing'
                    ? ` · footing ${seg.footing_quality ? `${seg.footing_quality}/10` : 'not rated'}` : '';
                  layer.bindTooltip(label + footing, { sticky: true });
                  layer.on('click', () => handleSegmentClick(seg));
                }}
              />
            </React.Fragment>
          ))}

          {drawing && !drawForm && (
            <DrawLayer points={drawPoints} onAdd={pt => setDrawPoints(prev => [...prev, pt])} />
          )}
        </MapContainer>

        {drawing && !drawForm && (
          <div className="draw-toolbar">
            <span>Tap the map along the proposed trail — <strong>{drawPoints.length}</strong> point{drawPoints.length === 1 ? '' : 's'}</span>
            <button className="btn-sm btn-secondary" disabled={!drawPoints.length}
              onClick={() => setDrawPoints(prev => prev.slice(0, -1))}>Undo</button>
            <button className="btn-sm btn-secondary" onClick={cancelDrawing}>Cancel</button>
            <button className="btn-sm btn-primary" disabled={drawPoints.length < 2}
              onClick={() => setDrawForm({ trail_name: '', description: '', expansion_status: 'identified' })}>Finish</button>
          </div>
        )}

        {drawForm && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3 style={{ marginTop: 0 }}>New expansion segment</h3>
              <p style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                Visible to volunteers and admins only. Length is worked out from the line you drew.
              </p>
              {drawError && <div className="alert alert-error">{drawError}</div>}
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>Trail name
                <input type="text" list="expansion-trail-names" value={drawForm.trail_name} autoFocus
                  onChange={e => setDrawForm({ ...drawForm, trail_name: e.target.value })}
                  placeholder="An existing trail, or a new name"
                  style={{ width: '100%', padding: '0.4rem' }} />
                <datalist id="expansion-trail-names">
                  {[...new Set(segments.map(s => s.trail_name))].sort().map(name => <option key={name} value={name} />)}
                </datalist>
              </label>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>Description
                <textarea rows={2} value={drawForm.description}
                  onChange={e => setDrawForm({ ...drawForm, description: e.target.value })}
                  style={{ width: '100%', padding: '0.4rem' }} />
              </label>
              <label style={{ display: 'block', marginBottom: '0.75rem' }}>Status
                <select value={drawForm.expansion_status}
                  onChange={e => setDrawForm({ ...drawForm, expansion_status: e.target.value })}
                  style={{ width: '100%', padding: '0.4rem' }}>
                  {EXPANSION_STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn-sm btn-primary" onClick={saveDrawing}
                  disabled={savingDraw || !drawForm.trail_name.trim()}>
                  {savingDraw ? 'Saving…' : 'Save segment'}
                </button>
                <button className="btn-sm btn-secondary" onClick={() => setDrawForm(null)} disabled={savingDraw}>Back to drawing</button>
                <button className="btn-sm btn-secondary" style={{ marginLeft: 'auto' }} onClick={cancelDrawing} disabled={savingDraw}>Discard</button>
              </div>
            </div>
          </div>
        )}

        {/* Layer controls — volunteers/admins only */}
        {!isNeighbor && (
          <div className={`map-layers ${layersCollapsed ? 'collapsed' : ''}`}>
            <div className="panel-header" onClick={() => setLayersCollapsed(!layersCollapsed)}>
              <h4>Trail Layers</h4>
              <span className="collapse-btn">{layersCollapsed ? '▶' : '▼'}</span>
            </div>
            {!layersCollapsed && (
              <>
                <select
                  value={selectedTrailFilter}
                  onChange={e => {
                    const val = e.target.value;
                    setSelectedTrailFilter(val);
                    setHighlightTrail(val || null);
                  }}
                  style={{ width: '100%', padding: '0.3rem', fontSize: '0.8rem', marginBottom: '0.5rem', border: '1px solid #ced4da', borderRadius: '4px' }}
                >
                  <option value="">— All Trails —</option>
                  {[...new Set(segments.map(s => s.trail_name))].sort().map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
                {Object.entries(CATEGORY_LABELS).map(([cat, label]) => (
                  <label key={cat} className="layer-toggle">
                    <input type="checkbox" checked={visibleCategories[cat]}
                      onChange={() => toggleCategory(cat)} />
                    <span className="layer-swatch"
                      style={{ backgroundColor: cat === 'internal' ? NEIGHBOR_TRAIL_COLOR : CATEGORY_COLORS[cat] }} />
                    <span>{label} ({categoryCounts[cat] || 0})</span>
                  </label>
                ))}
                <hr style={{ margin: '6px 0', borderColor: '#ddd' }} />
                <label className="layer-toggle">
                  <input type="checkbox" checked={showParcels}
                    onChange={() => setShowParcels(p => !p)} />
                  <span className="layer-swatch" style={{ backgroundColor: '#b5651d' }} />
                  <span>Parcels</span>
                </label>
                <label className="layer-toggle">
                  <input type="checkbox" checked={baseLayer === 'satellite'}
                    onChange={() => setBaseLayer(b => b === 'satellite' ? 'street' : 'satellite')} />
                  <span className="layer-swatch" style={{ backgroundColor: '#3a5a3a' }} />
                  <span>Satellite</span>
                </label>
                {isVolunteer && (
                  <button className="btn-sm btn-secondary" style={{ width: '100%', marginTop: '0.5rem' }}
                    onClick={startDrawing} disabled={drawing}>
                    ✏️ Draw expansion trail
                  </button>
                )}
                {isVolunteer && (
                  <div className="color-mode-toggle">
                    <h4>Color By</h4>
                    {Object.entries(COLOR_MODE_LABELS).map(([mode, label]) => (
                      <label key={mode} className="layer-toggle">
                        <input type="radio" name="colorMode" value={mode}
                          checked={colorMode === mode} onChange={() => setColorMode(mode)} />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Layer toggles for neighbors (volunteers/admins have these in Trail Layers) */}
        {isNeighbor && (
          <div className="map-layers" style={{ minWidth: 'auto' }}>
            <label className="layer-toggle" style={{ margin: 0 }}>
              <input type="checkbox" checked={showParcels}
                onChange={() => setShowParcels(p => !p)} />
              <span className="layer-swatch" style={{ backgroundColor: '#b5651d' }} />
              <span>Parcels</span>
            </label>
            <label className="layer-toggle" style={{ margin: 0 }}>
              <input type="checkbox" checked={baseLayer === 'satellite'}
                onChange={() => setBaseLayer(b => b === 'satellite' ? 'street' : 'satellite')} />
              <span className="layer-swatch" style={{ backgroundColor: '#3a5a3a' }} />
              <span>Satellite</span>
            </label>
          </div>
        )}

        {/* Legend */}
        <div className={`map-legend ${legendCollapsed ? 'collapsed' : ''}`}>
          <div className="panel-header" onClick={() => setLegendCollapsed(!legendCollapsed)}>
            <h4>{isNeighbor ? 'La Cresta Trails' : (activeColorMode === 'access_type' ? 'Access Types' : COLOR_MODE_LABELS[activeColorMode])}</h4>
            <span className="collapse-btn">{legendCollapsed ? '▶' : '▼'}</span>
          </div>
          {!legendCollapsed && (
            <>
              {isNeighbor ? (
                <>
                  <div className="legend-item">
                    <span className="legend-swatch" style={{ backgroundColor: NEIGHBOR_TRAIL_COLOR }} />
                    <span>Community Trails ({totalInternal})</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-swatch" style={{ backgroundColor: CATEGORY_COLORS.external }} />
                    <span>Roadside Trails ({categoryCounts['external'] || 0})</span>
                  </div>
                </>
              ) : activeColorMode === 'footing' ? (
                <>
                  {FOOTING_BANDS.map(band => (
                    <div key={band.label} className="legend-item">
                      <span className="legend-swatch" style={{ backgroundColor: footingColor(band.score) }} />
                      <span>{band.label}</span>
                    </div>
                  ))}
                  <div className="legend-item">
                    <span className="legend-swatch" style={{ backgroundColor: FOOTING_UNRATED_COLOR }} />
                    <span>Not rated ({segments.filter(s => s.footing_quality == null).length})</span>
                  </div>
                </>
              ) : activeColorMode === 'access_type' ? (
                <>
                  {(() => {
                    const seen = new Map();
                    segments.filter(s => s.access_type_color && s.access_type_name).forEach(s => {
                      if (!seen.has(s.access_type_name)) seen.set(s.access_type_name, s.access_type_color);
                    });
                    const entries = Array.from(seen.entries()).map(([name, color]) => (
                      <div key={name} className="legend-item">
                        <span className="legend-swatch" style={{ backgroundColor: color }} /><span>{name}</span>
                      </div>
                    ));
                    // Red is meaningless without saying what it means.
                    const noneCount = segments.filter(s => !s.access_type_name).length;
                    if (noneCount > 0) {
                      entries.push(
                        <div key="__none" className="legend-item">
                          <span className="legend-swatch" style={{ backgroundColor: NO_AGREEMENT_COLOR }} />
                          <span>No agreement ({noneCount})</span>
                        </div>
                      );
                    }
                    return entries;
                  })()}
                </>
              ) : (
                <>
                  {Object.entries(STATUS_COLORS).map(([s, c]) => (
                    <div key={s} className="legend-item">
                      <span className="legend-swatch" style={{ backgroundColor: c }} />
                      <span>{s.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </>
              )}
              {isVolunteer && (
                <div className="legend-item">
                  <span className="legend-swatch dashed" /><span>Active concern</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Segments without geometry */}
        {segments.filter(s => !s.geometry && visibleCategories[s.category]).length > 0 && (
          <div className="segments-no-geo">
            <h4>No map data</h4>
            {segments.filter(s => !s.geometry && visibleCategories[s.category]).map(seg => (
              <button key={seg.id} className="segment-list-item" onClick={() => handleSegmentClick(seg)}>
                {segmentName(seg)}
              </button>
            ))}
          </div>
        )}
      </div>

      {detailSegment && (
        <SegmentPanel segment={detailSegment} onClose={handlePanelClose} onUpdate={handleSegmentUpdate} />
      )}
    </div>
  );
}
